import { getMessage, getMessageList } from '@/utils/messages'

export interface TourStep {
  /** CSS selector for the element to spotlight, e.g. `[data-tour="summary-tiles"]` */
  selector: string
  title: string
  /** A plain string renders as a paragraph; an array renders as a bulleted list — use an
   * array whenever the step covers more than one distinct idea, for easier scanning. */
  description: string | string[]
  /** Route the step's target lives on. If different from the current route, the tour navigates there before spotlighting. */
  route?: string
  /** Section id (from SECTIONS) to force-expand before spotlighting, if it isn't already. */
  sectionId?: string
  /** Polled while this step is active; once it returns true the tour auto-advances. */
  autoAdvanceWhen?: () => boolean
  /** Message shown when Next is clicked while `autoAdvanceWhen` is still false. */
  validationMessage?: string
  /**
   * When set, the tour does not auto-advance via its own Next button — it waits for the
   * user to perform the real action (e.g. clicking "New Claim") and advances once the
   * resulting route starts with this prefix. Used where the real click carries essential
   * setup (e.g. clearing the previous draft) that a plain `navigate()` call would skip.
   */
  waitForRoutePrefix?: string
  /**
   * CSS selector for a sub-element within the spotlighted target that should remain
   * clickable, while the rest of the spotlighted area is sealed off. Used for steps where
   * only one specific control (e.g. the Vehicle Type field) should be interactive — every
   * other step is narration-only and must be advanced via Next/Previous.
   */
  interactiveSelector?: string
  /** Called once, right when this step's target is first found — used for tour-driven
   * side effects (e.g. auto-selecting a sample Vehicle Type) rather than requiring the
   * user to perform the action themselves. */
  onActivate?: () => void
  /** Forces the tooltip above the target instead of the default right/left/below placement —
   * used for targets tall enough (e.g. the claims table + pagination) that a below/beside
   * placement would push the tooltip's footer buttons off-screen. */
  placement?: 'above'
}

/** Selects Truck/Tractor in the Vehicle Type dropdown (falling back to the first real option
 * if that label isn't present), using the native value setter + a real 'change' event so
 * React's own onChange handler runs — this ensures the full real selection logic fires
 * (resetting dependent sections, clearing photos/AI data, etc.), not just a raw store
 * mutation that would leave the app out of sync. */
function autoSelectSampleVehicleType() {
  const select = document.querySelector('[data-testid="field-vehicleType"]') as HTMLSelectElement | null
  if (!select || (select.value && select.value !== 'Select')) return

  const options = Array.from(select.options)
  const sampleOption = options.find((o) => o.value === 'Truck/Tractor')
    ?? options.find((o) => o.value && o.value !== 'Select')
  if (!sampleOption) return

  const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')?.set
  nativeSetter?.call(select, sampleOption.value)
  select.dispatchEvent(new Event('change', { bubbles: true }))
}

export const dashboardTourSteps: TourStep[] = [
  {
    selector: '[data-tour="summary-tiles"]',
    title: getMessage('tour.steps.dashboard_overview.title'),
    description: getMessage('tour.steps.dashboard_overview.description'),
    route: '/dashboard',
  },
  {
    selector: '[data-tour="search-box"]',
    title: getMessage('tour.steps.search_claims.title'),
    description: getMessage('tour.steps.search_claims.description'),
    route: '/dashboard',
  },
  {
    selector: '[data-tour="vehicle-type-filter"]',
    title: getMessage('tour.steps.filter_results.title'),
    description: getMessage('tour.steps.filter_results.description'),
    route: '/dashboard',
  },
  {
    selector: '[data-tour="claims-table"]',
    title: getMessage('tour.steps.claims_list.title'),
    description: getMessageList('tour.steps.claims_list.description'),
    route: '/dashboard',
    placement: 'above',
  },
  {
    selector: '[data-tour="new-claim-button"]',
    title: getMessage('tour.steps.new_claim.title'),
    description: getMessage('tour.steps.new_claim.description'),
    route: '/dashboard',
  },
  {
    selector: '[data-tour="valuation-report-upload"]',
    title: getMessage('tour.steps.valuation_report.title'),
    description: getMessageList('tour.steps.valuation_report.description'),
    route: '/form',
    sectionId: 'valuation-report',
  },
  {
    selector: '#section-basic-information',
    title: getMessage('tour.steps.basic_information.title'),
    description: getMessage('tour.steps.basic_information.description'),
    route: '/form',
    sectionId: 'basic-information',
  },
  {
    selector: '[data-testid="field-vehicleType"]',
    title: getMessage('tour.steps.vehicle_type.title'),
    description: getMessage('tour.steps.vehicle_type.description'),
    route: '/form',
    sectionId: 'basic-information',
    // A default is auto-selected for the tour (via onActivate), but the field itself is
    // sealed off like every other narration-only step — the user advances with Next/Previous,
    // not by interacting with the field directly.
    onActivate: autoSelectSampleVehicleType,
  },
  {
    selector: '[data-tour="capture-upload-options"]',
    title: getMessage('tour.steps.capture_upload.title'),
    description: getMessageList('tour.steps.capture_upload.description'),
    route: '/form',
    sectionId: 'standard-equipment',
  },
  {
    // Truck/Tractor vehicles expose this field as "use" (Use), Trailers expose it as "type"
    // (Type) — the two are mutually exclusive per vehicle type, so only one is ever in the DOM.
    selector: '[data-testid="field-use"], [data-testid="field-type"]',
    title: getMessage('tour.steps.standard_equipment.title'),
    description: getMessage('tour.steps.standard_equipment.description'),
    route: '/form',
    sectionId: 'standard-equipment',
  },
  {
    selector: '[data-tour="save-draft-button"]',
    title: getMessage('tour.steps.save_draft.title'),
    description: getMessage('tour.steps.save_draft.description'),
    route: '/form',
    placement: 'above',
  },
  {
    selector: '[data-tour="submit-button"]',
    title: getMessage('tour.steps.submit.title'),
    description: getMessage('tour.steps.submit.description'),
    route: '/form',
    placement: 'above',
  },
  {
    selector: '[data-tour="dashboard-logo"]',
    title: getMessage('tour.steps.back_to_dashboard.title'),
    description: getMessage('tour.steps.back_to_dashboard.description'),
    route: '/form',
  },
]
